import React, { useMemo, lazy } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { getRunDateString, getWeekNumber } from '../utils/dateUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const Line = lazy(() => import('react-chartjs-2').then(module => ({ default: module.Line })));

const ProgressChart = ({ runs, startDate, targetDate, targetKm, isMobile }) => {
  const chartData = useMemo(() => {
    const totalDays = (targetDate - startDate) / (1000 * 60 * 60 * 24);

    const progressByPeriod = runs.reduce((acc, run) => {
      const dateString = getRunDateString(run.date);
      if (!dateString) return acc;
      const period = isMobile ? getWeekNumber(dateString) : dateString;
      if (!acc[period]) acc[period] = { km: 0, date: dateString };
      acc[period].km += Number(run.kilometers);
      return acc;
    }, {});

    let cumulative = 0;
    const progressData = Object.entries(progressByPeriod)
      .sort(([a], [b]) => new Date(progressByPeriod[a].date) - new Date(progressByPeriod[b].date))
      .map(([period, { km, date }]) => {
        cumulative += km;
        return { period, kilometers: cumulative, date };
      });

    const sollWerte = progressData.map(({ date }) => {
      const currentDate = new Date(date);
      const daysSinceStart = (currentDate - startDate) / (1000 * 60 * 60 * 24);
      return Math.min((daysSinceStart / totalDays) * targetKm, targetKm);
    });

    const labels = progressData.map(d => {
      if (isMobile) {
        const weekNum = d.period.split('-W')[1];
        return `KW ${weekNum}`;
      }
      return new Date(d.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
    });

    return {
      labels,
      datasets: [
        {
          label: 'Ist-Kilometer',
          data: progressData.map(d => d.kilometers),
          borderColor: '#3498db',
          tension: 0.4,
          pointRadius: isMobile ? 4 : 2,
        },
        {
          label: 'Soll-Kilometer',
          data: sollWerte,
          borderColor: '#e74c3c',
          borderDash: [5, 5],
          tension: 0,
          pointRadius: isMobile ? 4 : 2,
        }
      ]
    };
  }, [runs, startDate, targetDate, targetKm, isMobile]);

  return (
    <div className="chart-container">
      <h2>Fortschrittsverlauf</h2>
      <Line
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                font: { size: isMobile ? 10 : 12 },
                boxWidth: isMobile ? 15 : 40
              }
            },
            title: {
              display: true,
              text: isMobile ? 'Soll-Ist (wöchentlich)' : 'Soll-Ist-Vergleich',
              font: { size: isMobile ? 12 : 16 }
            }
          },
          scales: {
            x: {
              ticks: {
                maxRotation: isMobile ? 45 : 0,
                minRotation: isMobile ? 45 : 0,
                font: { size: isMobile ? 9 : 11 }
              }
            },
            y: {
              beginAtZero: true,
              title: {
                display: !isMobile,
                text: 'Kilometer',
                font: { size: isMobile ? 10 : 12 }
              },
              ticks: { font: { size: isMobile ? 9 : 11 } }
            }
          }
        }}
      />
    </div>
  );
};

export default ProgressChart;
